import type { ReactElement } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text, View, useWindowDimensions } from 'react-native'
import { tv } from 'tailwind-variants'

import { MAX_CONTENT_WIDTH } from '@flows-ui/constants'
import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { InfoBox as InfoBoxProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import { HTMLRenderer, body2BaseStyle } from '@ui-kit/components/HTMLRenderer'
import InfoNote from '@ui-kit/components/InfoNote'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { COLOR } from '@ui-kit/constants'
import { body2 } from '@ui-kit/core/typography'
import { borderColor } from '@ui-kit/styles/colors'
import { theme } from '@ui-kit/theme'

const infoBox = tv({
  slots: {
    container: '-ml-4',
    contentContainer: 'flex flex-shrink flex-col gap-x-2 rounded-lg px-1',
  },
  variants: {
    hasModalContent: {
      true: {
        title: 'max-w-85%',
      },
    },
  },
})

export const InfoBox = <TProps extends InfoBoxProps<any, any, any>>(
  props: TProps & { flowValues: FlowValues; children?: ReactElement }
) => {
  const windowDimensions = useWindowDimensions()
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })

  const { contentContainer } = infoBox()
  let children = props.children
  if (props.text) {
    const containerWidth =
      windowDimensions.width > MAX_CONTENT_WIDTH ? MAX_CONTENT_WIDTH : windowDimensions.width
    const contentWidth = containerWidth - theme.space.gutter * 2
    const title = props.title ? translateFunction(props.title) : ''
    const text = translateFunction(props.text)
    children = (
      <>
        {title ? <Text className={body2({ bold: true })}>{title}</Text> : null}
        <HTMLRenderer
          contentWidth={contentWidth}
          source={{ html: text }}
          baseStyle={{ ...body2BaseStyle, color: props.color ? COLOR[props.color] : COLOR.black }}
        />
      </>
    )
  }

  const icon = props.showInfoIcon ? (
    <PhosphorIcon
      name={props.iconName ?? 'Info'}
      size={24}
      weight={props.iconWeight}
      color={props.iconColor ? COLOR[props.iconColor] : undefined}
    />
  ) : undefined

  return (
    <InfoNote bgColor={props.bgColor} borderColor={props.borderColor} icon={icon}>
      <View
        className={contentContainer({
          className: [
            borderColor({
              color: props.borderColor,
            }),
            props.borderColor && '-mx-4',
          ],
        })}
      >
        {children}
      </View>
    </InfoNote>
  )
}
